/**
 * Product spec / configuration options.
 *
 * Options are shaped so the modal form can render dropdowns directly and
 * `defaultSpec()` gives buyers a sensible starting configuration for the
 * product they clicked on.
 */

// -------- Application (top-level Internal / External toggle) --------
export const APPLICATIONS = ["Internal", "External"];

// -------- Profiles, scoped by application --------
export const PROFILE_OPTIONS = [
  { value: "W20", label: "W20 Steel", usage: "Internal" },
  { value: "F50 Slim Tube", label: "F50 Slim Tube", usage: "Internal" },
  { value: "W40", label: "W40 Steel", usage: "External" },
  { value: "W50TB", label: "W50 TB Steel", usage: "External" },
  { value: "ALES65", label: "ALES65 Aluminium", usage: "External" },
];

export const DEFAULT_PROFILE_BY_APPLICATION = {
  Internal: "W20",
  External: "W40",
};

export const DEFAULT_APPLICATION_BY_CATEGORY = {
  "steel-windows": "External",
  "steel-doors": "External",
  "luxury-entrances": "External",
};

// -------- Glazing (Solid Steel Panel removed) --------
export const GLAZING_OPTIONS = [
  "Single Glazed",
  "Double Glazed",
  "Low-E DGU",
  "Reeded (fluted)",
  "Frosted",
  "Flemish",
];

// -------- Handle / Lock / Electronic --------
// Doors (steel-doors, luxury-entrances): pulls, levers, T-bars, D-pulls, half-moon, etc.
export const HANDLE_TYPES = [
  "Lever Handle",
  "Pull Handle",
  "Custom Pull Handle",
  "Half Moon Pull Handle",
  "Flush Pull Handle",
  "Recessed Handle",
  "T-Bar Handle",
  "D-Pull Handle",
  "Barn Door Pull",
  "Kick Plate (no handle)",
  "None",
];

// Windows (steel-windows): espagnolette latches, casement fasteners, monkey-tail,
// cockspur, friction stays, tilt-turn latches — NOT door pulls.
export const WINDOW_HANDLE_TYPES = [
  "Espagnolette Handle",
  "Monkey-Tail Latch",
  "Swan-Neck Handle",
  "Cockspur Handle",
  "Casement Fastener (peg-stay)",
  "Tilt-Turn Handle",
  "Friction Stay (concealed)",
  "Awning Stay",
  "Concealed Restrictor",
  "None",
];

export const HANDLE_DETAILS = [
  "Standard",
  "Black",
  "Satin",
  "Brass",
  "Antique Brass",
  "250mm",
  "300mm",
  "400mm",
  "600mm",
  "900mm",
  "1200mm",
  "TBC",
];

// Windows are fixed geometry hardware — no size choice, only finish.
export const WINDOW_HANDLE_DETAILS = [
  "Standard",
  "Black",
  "Satin",
  "Polished Chrome",
  "Brass",
  "Antique Brass",
  "Bronze",
  "TBC",
];

// Doors: mortice + multipoint locks etc.
export const LOCK_LATCH_TYPES = [
  "Lock Cylinder",
  "Passage Latch",
  "Privacy Latch",
  "Mortice Deadlock",
  "Multipoint Lock",
  "Roller Latch",
  "Magnetic Latch",
  "None",
];

// Windows: cam locks, restrictors, espagnolette locks — window-specific.
export const WINDOW_LOCK_LATCH_TYPES = [
  "Espagnolette Lock (multipoint)",
  "Cockspur Lock",
  "Cam Lock",
  "Key-Locking Handle",
  "Restrictor Lock (safety)",
  "Friction Stay (no lock)",
  "None",
];

// Category-aware helpers so the modal picks the correct taxonomy per product.
export function handleTypesFor(catKey) {
  return catKey === "steel-windows" ? WINDOW_HANDLE_TYPES : HANDLE_TYPES;
}
export function handleDetailsFor(catKey) {
  return catKey === "steel-windows" ? WINDOW_HANDLE_DETAILS : HANDLE_DETAILS;
}
export function lockLatchTypesFor(catKey) {
  return catKey === "steel-windows" ? WINDOW_LOCK_LATCH_TYPES : LOCK_LATCH_TYPES;
}

export const ELECTRONIC_LOCK_MODELS = [
  "None — Standard Lock Only",
  "Aqara U400",
  "Visage Zeno",
  "Yale Assure Lock 2",
  "Other (specify in notes)",
];

// -------- Locks (legacy — retained for older fields) --------
export const LOCK_OPTIONS = [
  "Key Lock (mortice)",
  "Multipoint Lock",
  "Electronic Lock",
  "Deadbolt",
  "Other (specify)",
];

// -------- Hardware --------
// Two lists per category:
//   `catalogue` — items also sold as products in the Hardware category (real codes).
//   `pull`      — generic pull styles that require a size (mm).
// Both are surfaced in the modal so a buyer picks either a catalogue item OR a
// pull with a specified length.
export const HARDWARE_CATALOGUE = {
  "steel-windows": [
    { value: "ES-WH07", label: "ES-WH07 — Swan-Neck Classical" },
    { value: "ES-WH08", label: "ES-WH08 — Cottage Flat Lever" },
    { value: "ES-WH01", label: "ES-WH01 — Espagnolette Graphite" },
    { value: "ES-WH03", label: "ES-WH03 — Espagnolette Antique Brass" },
    { value: "ES-WH13", label: "ES-WH13 — Period Tapered" },
    { value: "ES-WH15", label: "ES-WH15 — Swan-Neck Espagnolette" },
  ],
  "steel-doors": [
    { value: "ES-DH01", label: "ES-DH01 — Art Deco Lever" },
    { value: "ES-DH02", label: "ES-DH02 — Sleek Square" },
    { value: "ES-DH03", label: "ES-DH03 — Tapered Lever" },
    { value: "ES-DH04", label: "ES-DH04 — Modern Vertical Bar" },
    { value: "ES-DH05", label: "ES-DH05 — Classical Curved" },
  ],
  "luxury-entrances": [
    { value: "ES-DH04", label: "ES-DH04 — Modern Vertical Bar" },
  ],
};

export const HARDWARE_PULLS = {
  "steel-doors": [
    "Pull Handle",
    "Half-Moon Pull",
    "Rod Pull",
  ],
  "luxury-entrances": [
    "Bespoke Pull",
    "Half-Moon Pull",
    "Rod Pull",
    "Full-Height Bar",
  ],
  "steel-windows": [],
};

// Returns the full option list — catalogue items first, then pulls (grouped in
// the JSX with a divider). Each entry is `{ value, label, kind }`.
export function hardwareOptionsFor(catKey) {
  const cat = HARDWARE_CATALOGUE[catKey] || [];
  const pulls = HARDWARE_PULLS[catKey] || [];
  return [
    ...cat.map((o) => ({ ...o, kind: "catalogue" })),
    ...pulls.map((p) => ({ value: p, label: p, kind: "pull" })),
  ];
}

export function isPullHardware(value, catKey) {
  return (HARDWARE_PULLS[catKey] || []).includes(value);
}

// -------- Fixing --------
export const FIXING_OPTIONS = [
  "Tabs on outside",
  "Tabs on inside",
  "Pre-drilled holes through jamb centre",
  "Face-fixed to structural opening",
];

// -------- Threshold (external openings only) --------
export const THRESHOLD_OPTIONS = [
  "Standard Threshold",
  "Low-Profile Threshold",
  "Weathered Threshold",
];

// -------- Opening + Handing --------
export const OPENING_OPTIONS = {
  "steel-windows": ["Fixed", "Casement", "Awning", "Bifold", "Tilt-Turn"],
  "steel-doors": ["In-swing", "Out-swing", "Pivot", "Bifold"],
  "luxury-entrances": ["Pivot", "Swing"],
};

// Swing-type openings need a handing (Left/Right). Pivots and bifolds don't.
export const HANDING_OPTIONS = ["Left Hand", "Right Hand"];
export function needsHanding(opening) {
  if (!opening) return false;
  return /swing/i.test(opening);
}

// -------- Luxury panel note --------
export const LUXURY_PANEL_CONSTRUCTION = "Insulated Steel Panel";

// -------- Inference helpers --------
function inferOpening(name = "", categoryKey) {
  const n = name.toLowerCase();
  const opts = OPENING_OPTIONS[categoryKey] || [];
  if (n.includes("bi-fold") || n.includes("bifold")) return opts.find((o) => o === "Bifold");
  if (n.includes("pivot")) return opts.find((o) => o === "Pivot");
  if (n.includes("awning")) return opts.find((o) => o === "Awning");
  if (n.includes("casement")) return opts.find((o) => o === "Casement");
  if (n.includes("fixed")) return opts.find((o) => o === "Fixed");
  if (n.includes("tilt")) return opts.find((o) => o === "Tilt-Turn");
  return opts[0];
}

function inferGlazing(name = "") {
  const n = name.toLowerCase();
  if (n.includes("ribbed") || n.includes("fluted") || n.includes("reeded")) return "Reeded (fluted)";
  if (n.includes("frosted")) return "Frosted";
  if (n.includes("solid") || n.includes("stone panel") || n.includes("horizontal-panel")) return "Solid Steel Panel";
  return "Double Glazed";
}

export function isExternalProfile(profileValue) {
  const opt = PROFILE_OPTIONS.find((p) => p.value === profileValue);
  return opt?.usage === "External";
}

export function profilesFor(application) {
  return PROFILE_OPTIONS.filter((p) => p.usage === application);
}

/** Starting spec for a product — every field is user-editable in the modal. */
export function defaultSpec(product) {
  if (!product) return null;
  const cat = product.categoryKey;
  const application = DEFAULT_APPLICATION_BY_CATEGORY[cat] || "External";
  const profile = DEFAULT_PROFILE_BY_APPLICATION[application] || "W50TB";
  const opening = inferOpening(product.name, cat);
  const handing = needsHanding(opening) ? "Right Hand" : null;
  const glazing =
    cat === "luxury-entrances" ? "Solid Steel Panel" : inferGlazing(product.name);
  const first = hardwareOptionsFor(cat)[0];
  const hardware = first?.value || "";
  const lockDefault = cat === "steel-windows" ? "Key Lock (mortice)" : "Multipoint Lock";
  return {
    application,
    profile,
    opening,
    handing,
    glazing,
    hardware,
    lock: lockDefault,
    fixing: FIXING_OPTIONS[0],
    threshold: application === "External" ? THRESHOLD_OPTIONS[0] : null,
    panel: cat === "luxury-entrances" ? LUXURY_PANEL_CONSTRUCTION : null,
  };
}
